// =====================================================
// LEGACY AUDIT TYPES (Old System)
// =====================================================

export interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  operation: string;
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  user_id: string | null;
  timestamp: string;
  ip_address: string | null;
  user_agent: string | null;
}

// =====================================================
// OPTIMIZED AUDIT TYPES (New System)
// =====================================================

export interface ChangedField {
  old: any;
  new: any;
}

export enum AuditSource {
  SCRAPER = 'scraper',
  MANUAL = 'manual',
  SYSTEM = 'system',
}

export interface AuditLogOptimized {
  id: string;
  table_name: string;
  record_id: string;
  operation: AuditOperation;
  // `changed_fields` shape varies by operation:
  //   UPDATE                 -> Record<string, ChangedField>          (per-field old/new diff)
  //   INSERT                 -> Record<string, unknown>               (flat key→value of new record)
  //   SCRAP_NO_CHANGE / *    -> Record<string, unknown>               (e.g. { reason: "…" })
  //   DELETE                 -> {}                                    (empty object)
  // Renderers must branch on shape.
  changed_fields: Record<string, ChangedField | unknown> | null;
  change_summary: string | null;
  source: AuditSource | null;
  user_id: string | null;
  // Populated server-side via LEFT JOIN users (added 2026-05-18).
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_email?: string | null;
  user_role?: string | null;
  timestamp: string;
  ip_address: string | null;
  user_agent: string | null;
}

// =====================================================
// FILTER TYPES
// =====================================================

export interface AuditLogFilters {
  table?: string;
  operation?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface AuditLogOptimizedFilters {
  table?: string;
  operation?: string;
  source?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
  changedField?: string;
  recordId?: string;
  page?: number;
  limit?: number;
}

// Audited business fields surfaced as filter options. Mirrors the
// per-table allowlist in log_comprehensive_audit() (migration 0002).
export const AUDITED_FIELDS: { value: string; label: string }[] = [
  { value: 'sold_out', label: 'sold_out (Project)' },
  { value: 'sold_out_date', label: 'sold_out_date (Project)' },
  { value: 'status', label: 'status (Project / Unit)' },
  { value: 'name', label: 'name (Project)' },
  { value: 'website', label: 'website (Project)' },
  { value: 'developer_id', label: 'developer_id (Project)' },
  { value: 'broker_id', label: 'broker_id (Project)' },
  { value: 'architect_id', label: 'architect_id (Project)' },
  { value: 'error_classification', label: 'error_classification (Project)' },
  { value: 'monitoring_dismissed_at', label: 'monitoring_dismissed_at (Project)' },
  { value: 'current_price', label: 'current_price (Unit)' },
  { value: 'sqm_price', label: 'sqm_price (Unit)' },
  { value: 'sales_date', label: 'sales_date (Unit)' },
  { value: 'unit_ref', label: 'unit_ref (Unit)' },
  { value: 'email', label: 'email (User)' },
  { value: 'role', label: 'role (User)' },
  { value: 'is_active', label: 'is_active (User)' },
];

// Fields whose changes should highlight as critical (amber tint + warning icon).
export const CRITICAL_FIELDS = new Set([
  'sold_out',
  'role',
  'is_active',
  'website',
]);

// Anomaly summary payload from GET /audit-logs/anomalies (added 2026-05-18).
export interface AuditAnomalies {
  manualEditsLast24h: number;
  soldOutFlipsLast7d: number;
  roleChangesLast30d: number;
  recentDeletesLast7d: number;
}

// =====================================================
// RESPONSE TYPES
// =====================================================

export interface AuditLogResponse {
  data: AuditLog[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AuditLogOptimizedResponse {
  data: AuditLogOptimized[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// =====================================================
// STATISTICS TYPES
// =====================================================

export interface AuditStats {
  totalLogs: number;
  logsByOperation: Record<string, number>;
  logsByTable: Record<string, number>;
  recentActivity: AuditLog[];
}

export interface AuditOptimizedStats {
  totalLogs: number;
  operationStats: Record<string, number>;
  sourceStats: Record<string, number>;
  tableStats: Record<string, number>;
  recentActivity: AuditLogOptimized[];
}

export interface StorageStats {
  oldTable: {
    recordCount: number;
    storageSize: number;
    storageSizeFormatted: string;
  };
  newTable: {
    recordCount: number;
    storageSize: number;
    storageSizeFormatted: string;
  };
  reduction: {
    records: number;
    storage: number;
    percentage: number;
  };
}

export type AuditOperation = 
  | 'INSERT'
  | 'UPDATE'
  | 'DELETE'
  | 'TRUNCATE'
  | 'SCRAP_INSERT'
  | 'SCRAP_UPDATE'
  | 'SCRAP_PROJECT_MISSING'
  | 'SCRAP_PROJECT_AMBIGUOUS'
  | 'SCRAP_UNKNOWN_STATUS'
  | 'SCRAP_INVALID_STATUS'
  | 'SCRAP_ERROR'
  | 'SCRAP_SKIPPED'
  | 'SCRAP_WARNING'
  | 'SCRAP_PROCESSED'
  | 'SCRAP_NO_CHANGE'
  | 'SCRAP_PRICE_NULL';

export type AuditTable = 
  | 'project'
  | 'unit'
  | 'architect'
  | 'developer'
  | 'broker'
  | 'webscrap_staging'
  | 'users';

export const AUDIT_OPERATIONS: Record<AuditOperation, string> = {
  INSERT: 'Insert',
  UPDATE: 'Update',
  DELETE: 'Delete',
  TRUNCATE: 'Truncate',
  SCRAP_INSERT: 'Scrap Insert',
  SCRAP_UPDATE: 'Scrap Update',
  SCRAP_PROJECT_MISSING: 'Scrap Project Missing',
  SCRAP_PROJECT_AMBIGUOUS: 'Scrap Project Ambiguous',
  SCRAP_UNKNOWN_STATUS: 'Scrap Unknown Status',
  SCRAP_INVALID_STATUS: 'Scrap Invalid Status',
  SCRAP_ERROR: 'Scrap Error',
  SCRAP_SKIPPED: 'Scrap Skipped',
  SCRAP_WARNING: 'Scrap Warning',
  SCRAP_PROCESSED: 'Scrap Processed',
  SCRAP_NO_CHANGE: 'Scrap No Change',
  SCRAP_PRICE_NULL: 'Scrap Price Null',
};

export const AUDIT_TABLES: Record<AuditTable, string> = {
  project: 'Projects',
  unit: 'Units',
  architect: 'Architects',
  developer: 'Developers',
  broker: 'Brokers',
  webscrap_staging: 'Web Scraping',
  users: 'Users',
};

export const AUDIT_SOURCES: Record<AuditSource, string> = {
  [AuditSource.SCRAPER]: 'Scraper',
  [AuditSource.MANUAL]: 'Manual',
  [AuditSource.SYSTEM]: 'System',
};

export const OPERATION_COLORS: Record<AuditOperation, string> = {
  INSERT: 'bg-success-100 text-success-800',
  UPDATE: 'bg-primary-100 text-primary-800',
  DELETE: 'bg-danger-100 text-danger-800',
  TRUNCATE: 'bg-danger-100 text-danger-800',
  SCRAP_INSERT: 'bg-success-100 text-success-800',
  SCRAP_UPDATE: 'bg-primary-100 text-primary-800',
  SCRAP_PROJECT_MISSING: 'bg-warning-100 text-warning-800',
  SCRAP_PROJECT_AMBIGUOUS: 'bg-warning-100 text-warning-800',
  SCRAP_UNKNOWN_STATUS: 'bg-warning-100 text-warning-800',
  SCRAP_INVALID_STATUS: 'bg-warning-100 text-warning-800',
  SCRAP_ERROR: 'bg-danger-100 text-danger-800',
  SCRAP_SKIPPED: 'bg-secondary-100 text-secondary-800',
  SCRAP_WARNING: 'bg-warning-100 text-warning-800',
  SCRAP_PROCESSED: 'bg-success-100 text-success-800',
  SCRAP_NO_CHANGE: 'bg-secondary-100 text-secondary-800',
  SCRAP_PRICE_NULL: 'bg-warning-100 text-warning-800',
};

export const SOURCE_COLORS: Record<AuditSource, string> = {
  [AuditSource.SCRAPER]: 'bg-blue-100 text-blue-800',
  [AuditSource.MANUAL]: 'bg-green-100 text-green-800',
  [AuditSource.SYSTEM]: 'bg-gray-100 text-gray-800',
};
